<template>
  <div
    ref="inputWrapper"
    class="relative z-10 flex items-center w-full transition-all duration-200 rounded-lg"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
  >
    <t-textarea
      ref="input"
      :disabled="disabled"
      v-model="inputVal"
      :value="value ? value : ''"
      @input="input()"
      @blur="blur()"
      @change="change"
      :type="inputType"
      :min="min"
      :max="max"
      :step="step"
      :placeholder="placeholder"
      :maxLength="maxLength"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      important
      class="placeholder:text-gray min-h-6 max-h-[10.25rem] w-full leading-5 text-black bg-white"
      :class="{'text-gray': disabled, 'pointer-events-none': actAsButton}"
    />
    <div ref="rightSide" class="right-2 bottom-4 absolute cursor-pointer">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="break-word absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  export default {
    name: 'transparent-input',
    props: {
      pattern: {
        type: String,
        default: ''
      },
      actAsButton: {
        type: Boolean,
        default: false
      },
      labelClass: String,
      inputType: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      autocomplete: String,
      value: [String, Number, Array],
      min: [String, Number],
      max: [String, Number],
      step: [String, Number],
      placeholder: {
        type: String,
        default: ''
      },
      maxLength: {
        type: Number,
        required: false
      },
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: Boolean,
      inputmode: {
        type: String,
        validator: v => ['none', 'numeric', 'tel', 'decimal', 'email', 'url', 'search'].indexOf(v) !== -1
      }
    },
    data() {
      return {
        inputVal: Array.isArray(this.value) ? this.value.join(', ') : this.value || '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        isValid: true
      }
    },
    async mounted() {
      await this.componentLoaded()

      this.$refs.input.$el.style.width = `calc(100% - ${this.$refs.rightSide.clientWidth}px - 1.5rem`
      if (this.inputType === 'number') {
        this.$refs.input.$el.addEventListener('keypress', e => this.secureNumberInput(e))
      }

      if (!this.preventAutoFocus && this.$refs.input.$el.value !== '') this.focus()

      setTimeout(() => {
        if (!this.preventAutoFocus && this.$refs.input.$el.value !== '') this.focus()
      }, 100)

      this.interval = setInterval(() => {
        let style = window.getComputedStyle(this.$refs.input.$el)

        if (style && (style.backgroundColor !== this.inputColor || style.backgroundImage !== 'none')) {
          this.$emit('isAutofilled')
          clearInterval(this.interval)
        }
      }, 333)
    },
    methods: {
      secureNumberInput(e) {
        if ((e.which !== 8 && e.which !== 0 && e.which !== 13 && e.which < 48) || e.which > 57) {
          e.preventDefault()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (
              this.$refs.input &&
              this.$refs.input.$el &&
              this.$refs.inputWrapper &&
              this.$refs.label &&
              this.$refs.rightSide &&
              this.$refs.error
            ) {
              clearInterval(interval)
              resolve()
            }
          }, 333)
        })
      },
      input() {
        if (this.pattern !== '') {
          const regex = new RegExp(this.pattern.replace(/'/g, ''))
          if (!regex.test(this.getValue())) {
            this.isValid = false
          } else {
            if (this.isValid) {
              this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
              this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
            }
            this.clearError()
          }
        }
        this.$emit('input', this.getValue())
      },
      focus() {
        this.$refs.input.$el.focus()
      },
      blur() {
        if (this.inputVal.length === 0 && this.label) {
          this.$refs.inputWrapper.classList.replace(['p-2'], ['px-2', 'py-4.5'])
          this.$refs.label.classList.replace(
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold'],
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal']
          )
          this.$refs.input.$el.classList.replace('bottom-2', 'bottom-4.5')
        }
        this.$emit('blur')
      },
      change($event) {
        const value = $event.target.value

        if (this.inputType === 'number' && this.min !== null && this.max !== null && this.min >= 0 && this.max >= 0) {
          const notEnough = value < this.min
          const tooMuch = value > this.max
          const err = notEnough ? `The minimum value is ${this.min}!` : tooMuch ? `The maximum value is ${this.max}!` : ''

          if (err) this.$toast.error(err)

          $event.target.value = notEnough ? +this.min : tooMuch ? +this.max : value
        } else {
          $event.target.value = value
        }

        this.$emit('change', $event.target.value)
      },
      getValue() {
        return Array.isArray(this.value) ? (this.value.length === 1 ? this.inputVal : this.value) : this.inputVal
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.$el.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.$el.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    watch: {
      value(newVal) {
        this.inputVal = Array.isArray(newVal) ? newVal.join(', ') : newVal || ''
        if (!this.preventAutoFocus && !this.disabled && (this.inputVal !== null || this.inputVal !== '')) this.focus()
        if (!this.disabled && (this.inputVal === null || this.inputVal === '')) this.blur()
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    beforeDestroy() {
      if (this.inputType === 'number') {
        this.$refs.input.$el.removeEventListener('keypress', e => this.secureNumberInput(e))
      }
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
